import React from 'react';
import { Box, FormControl, FormHelperText, FormLabel, Grid, Stack, TextField } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { get, isNil, some } from 'lodash';
import { createScopedI18n } from '../../i18n/i18n';
import { CreateGuestJobValueType } from './page';
import { MapsPickerCard, UploadImagePickerCard } from '../../components';
import FormActions from './form_actions';

interface RestaurantFormProps {
  form: UseFormReturn<CreateGuestJobValueType>;
  onNext: () => void;
  loading?: boolean;
}

const restaurantFieldI18n = createScopedI18n('graphql.fields.restaurant');
const restaurantCreatePageI18n = createScopedI18n('pages.restaurants_new');

const RestaurantForm: React.FC<RestaurantFormProps> = ({ form, onNext, loading }) => {
  const {
    control,
    setValue,
    trigger,
    formState: { errors: validateErrors },
  } = form;

  const photosInvalid = some(
    [validateErrors.photoFrontToken, validateErrors.photoAdd1Token, validateErrors.photoAdd2Token],
    (error) => !isNil(error),
  );

  const handleNext = async () => {
    const isValid = await trigger([
      'restaurantName',
      'landmark',
      'location',
      'photoFrontToken',
      'photoAdd1Token',
      'photoAdd2Token',
    ]);
    if (isValid) {
      onNext();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Controller
        key="restaurantName"
        control={control}
        name="restaurantName"
        render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
          <FormControl id={field.name} fullWidth error={invalid}>
            <Grid item container alignItems="center" gap={1}>
              <Grid item xs={3}>
                <FormLabel>{restaurantFieldI18n('name')} *</FormLabel>
              </Grid>
              <Grid item xs>
                <TextField
                  id={field.name}
                  type="text"
                  fullWidth
                  value={value ?? ''}
                  error={invalid}
                  helperText={error?.message}
                  {...field}
                />
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Controller
        key="branchName"
        control={control}
        name="branchName"
        render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
          <FormControl id={field.name} fullWidth error={invalid}>
            <Grid item container alignItems="center" gap={1}>
              <Grid item xs={3}>
                <FormLabel>{restaurantFieldI18n('branch_name')}</FormLabel>
              </Grid>
              <Grid item xs>
                <TextField
                  id={field.name}
                  type="text"
                  fullWidth
                  value={value ?? ''}
                  error={invalid}
                  helperText={error?.message}
                  {...field}
                />
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Controller
        key="landmark"
        control={control}
        name="landmark"
        render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
          <FormControl id={field.name} fullWidth error={invalid}>
            <FormLabel>{restaurantFieldI18n('landmark')} *</FormLabel>
            <TextField
              id={field.name}
              type="text"
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              value={value ?? ''}
              error={invalid}
              helperText={error?.message}
              {...field}
            />
          </FormControl>
        )}
      />

      <Controller
        key="location"
        control={control}
        name="location"
        render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
          <FormControl id={field.name} fullWidth error={invalid}>
            <FormLabel>{restaurantFieldI18n('location')} *</FormLabel>
            <MapsPickerCard
              location={value}
              onConfirm={(_e, result) => {
                if (result.location) {
                  onChange(result.location);
                }
                setValue('placeId', result.placeId ?? undefined);
              }}
              sx={{ aspectRatio: 2.5 }}
            />
            <FormHelperText variant="outlined">
              {get(error, ['latitude', 'message']) ?? get(error, ['longitude', 'message'])}
            </FormHelperText>
          </FormControl>
        )}
      />

      <FormControl fullWidth error={photosInvalid}>
        <Stack gap={0.5}>
          <FormLabel>{restaurantFieldI18n('photo')} *</FormLabel>
          <Controller
            key="photoFrontToken"
            control={control}
            name="photoFrontToken"
            render={({ field: { onChange, ...field }, fieldState: { error } }) => (
              <UploadImagePickerCard
                id={field.name}
                noAuthUpload
                buttonText={restaurantCreatePageI18n('select_photo', {
                  photo_name: restaurantFieldI18n('photos.photo_front'),
                })}
                errorMessage={error?.message}
                onUploadDone={(token) => onChange(token)}
                sx={{ aspectRatio: 2 }}
              />
            )}
          />
          <Stack flexDirection="row" gap={0.5}>
            <Controller
              key="photoAdd1Token"
              control={control}
              name="photoAdd1Token"
              render={({ field: { onChange, ...field }, fieldState: { error } }) => (
                <UploadImagePickerCard
                  id={field.name}
                  noAuthUpload
                  buttonText={restaurantCreatePageI18n('select_photo', {
                    photo_name: restaurantFieldI18n('photos.photo_add_1'),
                  })}
                  errorMessage={error?.message}
                  onUploadDone={(token) => onChange(token)}
                  sx={{ aspectRatio: 3 / 2 }}
                />
              )}
            />
            <Controller
              key="photoAdd2Token"
              control={control}
              name="photoAdd2Token"
              render={({ field: { onChange, ...field }, fieldState: { error } }) => (
                <UploadImagePickerCard
                  id={field.name}
                  noAuthUpload
                  buttonText={restaurantCreatePageI18n('select_photo', {
                    photo_name: restaurantFieldI18n('photos.photo_add_2'),
                  })}
                  errorMessage={error?.message}
                  onUploadDone={(token) => onChange(token)}
                  sx={{ aspectRatio: 3 / 2 }}
                />
              )}
            />
          </Stack>
        </Stack>
      </FormControl>

      <FormActions loading={loading} onNext={handleNext} />
    </Box>
  );
};

export default RestaurantForm;
